/**
 * UserGuideModal.jsx - Guía Oficial de Uso de DamyxIA
 * 
 * Documentación informativa para usuarios sobre cómo usar
 * el editor de imágenes, IA creativa y Área de Trabajo.
 */

import { useState } from 'react';
import { 
  X, BookOpen, Upload, Send, Layout, Paintbrush, Wand2, 
  Square, Images, XCircle, Save, Layers, ArrowRight,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const UserGuideModal = ({ isOpen, onClose, language: propLanguage }) => {
  const { t: globalT, language: contextLanguage } = useLanguage();
  const language = propLanguage || contextLanguage;
  
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  // Usar traducciones del contexto
  const t = (key) => globalT(`userGuide.${key}`);

  // Contenido de la guía - Versión Visual Premium
  const sections = [
    {
      icon: Upload,
      emoji: '📥',
      title: language === 'es' ? '1. Subir Imágenes' : '1. Upload Images',
      content: language === 'es' ? `Podés subir imágenes arrastrándolas al chat o usando el botón de subir archivo.

Cuando subís una imagen:
• Se muestra en el chat.
• Queda en estado "Lista para trabajar".
• No aparece en el Área de Trabajo hasta que lo pidas.` : `You can upload images by dragging them to the chat or using the upload button.

When you upload an image:
• It shows in the chat.
• It stays in IMAGE_READY_FOR_WORK state.
• It doesn't appear in the Workspace until you ask.`
    },
    {
      icon: Send,
      emoji: '➡️',
      title: language === 'es' ? '2. Enviar una Imagen al Área de Trabajo' : '2. Send Image to Workspace',
      content: language === 'es' ? `Para comenzar a editar, escribí en el chat:

• "Pásala al área de trabajo"
• "Replicala"
• "Quiero trabajar esta imagen"
• "Envíala al área de trabajo"
• "Convertirla en pintura / boceto / diseño"

DamyxIA:
• Publica la imagen en el Área de Trabajo.
• La convierte en la imagen activa.
• La deja lista para editar o transformar con IA.` : `To start editing, type in the chat:

• "Send it to the workspace"
• "Replicate it"
• "I want to work on this image"
• "Convert it to painting / sketch / design"

DamyxIA will:
• Publish the image to the Workspace.
• Make it the active image.
• Leave it ready to edit or transform with AI.`
    },
    {
      icon: Layout,
      emoji: '🖼️',
      title: language === 'es' ? '3. Área de Trabajo Profesional' : '3. Professional Workspace',
      content: language === 'es' ? `El Área de Trabajo es un espacio grande y limpio donde podés:

• Ver la imagen activa.
• Editarla con herramientas clásicas.
• Transformarla con IA.
• Crear variantes.
• Guardar, descargar o imprimir tus resultados.

Estructura visual:
• Zona central: imagen activa.
• Barra superior: herramientas principales.
• Barra lateral derecha: variantes generadas.` : `The Workspace is a large, clean space where you can:

• View the active image.
• Edit it with classic tools.
• Transform it with AI.
• Create variants.
• Save, download or print your results.

Visual structure:
• Central area: active image.
• Top bar: main tools.
• Right sidebar: generated variants.`
    },
    {
      icon: Paintbrush,
      emoji: '🎛️',
      title: language === 'es' ? '4. Herramientas de Edición (Canvas)' : '4. Editing Tools (Canvas)',
      content: language === 'es' ? `Herramientas disponibles:

• Rotar
• Voltear
• Recortar
• Redimensionar
• Zoom
• Brillo
• Contraste
• Saturación
• Claridad

Cada edición genera automáticamente una variante.` : `Available tools:

• Rotate
• Flip
• Crop
• Resize
• Zoom
• Brightness
• Contrast
• Saturation
• Clarity

Each edit automatically generates a variant.`
    },
    {
      icon: Wand2,
      emoji: '🤖',
      title: language === 'es' ? '5. Edición con IA (Vision_Replication)' : '5. AI Editing (Vision_Replication)',
      content: language === 'es' ? `Podés pedirle a DamyxIA:

• "Hazla más brillante"
• "Convierte esta foto en pintura"
• "Cambia el fondo por uno blanco"
• "Hazla más nítida"
• "Crea una versión estilo boceto"
• "Cambia el color de la camisa"
• "Elimina este objeto"

La IA:
• Analiza tu instrucción.
• Modifica la imagen.
• Crea una nueva variante.
• La muestra en la barra lateral.` : `You can ask DamyxIA:

• "Make it brighter"
• "Convert this photo to painting"
• "Change the background to white"
• "Make it sharper"
• "Create a sketch version"
• "Change the shirt color"
• "Remove this object"

The AI will:
• Analyze your instruction.
• Modify the image.
• Create a new variant.
• Show it in the sidebar.`
    },
    {
      icon: Square,
      emoji: '✂️',
      title: language === 'es' ? '6. Edición por Selección' : '6. Selection Editing',
      content: language === 'es' ? `Podés seleccionar una parte de la imagen con:

• Rectángulo
• Lazo

Y pedir:

• "Borra esto"
• "Sustituye esta parte por un árbol"
• "Rellena esta zona con césped"

La IA modifica solo la región seleccionada.` : `You can select a part of the image with:

• Rectangle
• Lasso

And ask:

• "Erase this"
• "Replace this part with a tree"
• "Fill this area with grass"

The AI modifies only the selected region.`
    },
    {
      icon: Images,
      emoji: '🗂️',
      title: language === 'es' ? '7. Galería de Imágenes (Botón "Imágenes")' : '7. Image Gallery ("Images" Button)',
      content: language === 'es' ? `En la barra superior del Área de Trabajo está el botón "Imágenes".

Al hacer clic:
• Se abre una ventana con TODAS tus imágenes:
  - Subidas
  - Publicadas
  - Variantes
• Cada imagen aparece como miniatura.
• Al seleccionarla:
  - Se convierte en la imagen activa.
  - Se cargan sus variantes.
  - La ventana se cierra automáticamente.

La ventana tiene una "X" para cerrarla.` : `In the Workspace top bar there's the "Images" button.

When clicked:
• A window opens with ALL your images:
  - Uploaded
  - Published
  - Variants
• Each image appears as a thumbnail.
• When selecting one:
  - It becomes the active image.
  - Its variants load.
  - The window closes automatically.

The window has an "X" to close it.`
    },
    {
      icon: XCircle,
      emoji: '❌',
      title: language === 'es' ? '8. Cerrar la Imagen Activa' : '8. Close Active Image',
      content: language === 'es' ? `En la esquina superior derecha del Área de Trabajo hay una "X".

Al hacer clic:
• Se cierra la imagen activa.
• No se borra del sistema.
• Podés abrir otra desde el botón "Imágenes".` : `In the top right corner of the Workspace there's an "X".

When clicked:
• The active image closes.
• It's not deleted from the system.
• You can open another from the "Images" button.`
    },
    {
      icon: Save,
      emoji: '💾',
      title: language === 'es' ? '9. Guardar, Descargar e Imprimir' : '9. Save, Download & Print',
      content: language === 'es' ? `Cada imagen (original o variante) tiene tres botones:

• Guardar → la persiste en tu proyecto.
• Descargar → la baja como PNG/JPG.
• Imprimir → abre el diálogo de impresión del navegador.` : `Each image (original or variant) has three buttons:

• Save → persists it in your project.
• Download → downloads it as PNG/JPG.
• Print → opens the browser print dialog.`
    },
    {
      icon: Layers,
      emoji: '🔁',
      title: language === 'es' ? '10. Variantes y Versiones' : '10. Variants & Versions',
      content: language === 'es' ? `Cada vez que editás o transformás una imagen:

• Se crea un nuevo elemento en el Área de Trabajo.
• Aparece en la barra lateral.
• Podés volver a cualquier versión con un clic.
• Podés generar infinitas variantes.` : `Every time you edit or transform an image:

• A new WorkspaceItem is created.
• It appears in the sidebar.
• You can go back to any version with one click.
• You can generate infinite variants.`
    },
    {
      icon: ArrowRight,
      emoji: '🔄',
      title: language === 'es' ? '11. Flujo Completo de Trabajo' : '11. Complete Workflow',
      content: language === 'es' ? `1. Subís una imagen al chat.
2. Le decís "pásala al área de trabajo".
3. La editás con herramientas o IA.
4. Generás variantes.
5. Guardás, descargás o imprimís.
6. Podés abrir cualquier imagen desde el botón "Imágenes".` : `1. Upload an image to the chat.
2. Say "send it to the workspace".
3. Edit it with tools or AI.
4. Generate variants.
5. Save, download or print.
6. You can open any image from the "Images" button.`
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[85vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-gradient-to-r from-indigo-900/30 to-purple-900/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {language === 'es' ? 'Guía Visual de Uso' : 'Visual User Guide'}
              </h2>
              <p className="text-xs text-zinc-400">
                {language === 'es' 
                  ? 'Edición de Imágenes con IA • Variantes • Área de Trabajo Pro' 
                  : 'AI Image Editing • Variants • Pro Workspace'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            data-testid="close-guide-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {sections.map((section, index) => (
              <div 
                key={index}
                className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-800/30 hover:border-zinc-700 transition-colors"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{section.emoji}</span>
                    <span className={`font-medium text-sm ${
                      expandedSection === index ? 'text-white' : 'text-zinc-300'
                    }`}>
                      {section.title}
                    </span>
                  </div>
                  {expandedSection === index ? (
                    <ChevronUp className="w-4 h-4 text-zinc-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-400" />
                  )}
                </button>

                {/* Section Content */}
                {expandedSection === index && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="pl-11 border-l-2 border-indigo-500/30 ml-2">
                      <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
                        {section.content}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-800 bg-zinc-900/80">
          <p className="text-xs text-zinc-500 text-center">
            {language === 'es' 
              ? 'DamyxIA - Tu asistente de edición de imágenes con IA' 
              : 'DamyxIA - Your AI-powered image editing assistant'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserGuideModal;
